import React, { forwardRef, useState, useEffect } from 'react';
import '../styles/main.scss';
import './PostCard.scss';
import { supabase } from '../lib/supabase';
import { formatDate } from '../utils/formatDate';
import { getPostComments, createComment } from '../api/api';
import { deleteComment } from '../api/api';

interface Props {
  id: number;
  title: string;
  content: string;
  type: string;
  author: string;
  created_at: string;
  image?: string | null;
  onDeletePost?: (id: number) => void;
}

interface Comment {
  id: number;
  text: string;
  created_at: string;
}

const PostCard = forwardRef<HTMLDivElement, Props>(({ id, title, content, type, created_at, image, onDeletePost }, ref) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');

  const [glowY, setGlowY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const el = document.querySelector(`[data-post-id="${id}"]`) as HTMLElement;
      if (!el) return;

      const rect = el.getBoundingClientRect();

      const viewportHeight = window.innerHeight;

      const progress =
        (viewportHeight - rect.top) / (viewportHeight + rect.height);

      setGlowY(Math.max(0, Math.min(1, progress)));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [id]);

  useEffect(() => {
    const channel = supabase
      .channel(`comments-post-${id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'comments',
          filter: `post_id=eq.${id}`,
        },
        (payload) => {
          const newComment = payload.new as Comment;

          setComments(prev => {
            const exists = prev.some(c => c.id === newComment.id);

            if (exists) return prev;

            const filtered = prev.filter(c =>
              !(c.text === newComment.text && c.id.toString().length > 12)
            );

            return [newComment, ...filtered];
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [id]);

  useEffect(() => {
    getPostComments(id)
      .then(setComments)
      .catch(console.error);
  }, [id]);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      await createComment({
        post_id: id,
        text: newComment,
      });

      setNewComment('');
    } catch (e) {
      console.error('Ошибка отправки комментария', e);
    }
  };

  // const handleDeletePost = async () => {
  //   const confirmed = window.confirm('Точно удалить пост?');

  //   if (!confirmed) return;

  //   try {
  //     await deletePost(id, '1234');

  //     onDeletePost?.(id);

  //     // window.location.reload();
  //   } catch (e) {
  //     console.error('Ошибка удаления поста', e);
  //   }
  // };

  const handleDeleteComment = async (commentId: number) => {
    const confirmed = window.confirm('Точно удалить комментарий?');

    if (!confirmed) return;

    try {
      await deleteComment(commentId, '1234');

      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch (e) {
      console.error('Ошибка удаления комментария', e);
    }
  };

  const toAppleMusicEmbed = (url: string) => {
    if (!url.includes('music.apple.com')) return url;
    let embedUrl = url.includes('embed.music.apple.com')
      ? url
      : url.replace('https://music.apple.com', 'https://embed.music.apple.com');
    embedUrl += embedUrl.includes('?') ? '&theme=dark' : '?theme=dark';
    return embedUrl;
  };

  const getAppleType = (url: string) => {
    if (!url.includes('music.apple.com')) return 'unknown';

    if (url.includes('/playlist/')) return 'playlist';

    if (url.includes('/album/')) {
      return url.includes('?i=') ? 'track' : 'album';
    }

    return 'unknown';
  };

  const renderMedia = (url: string) => {
    if (url.includes('music.apple.com')) {
      const type = getAppleType(url);

      let height = 150;

      if (type === 'album' || type === 'playlist') {
        height = 450;
      }

      return (
        <iframe
          allow="autoplay *; encrypted-media *;"
          height={height}
          title={`apple-music-${url}`}
          style={{
            width: '100%',
            maxWidth: 660,
            border: 'none',
            overflow: 'hidden'
          }}
          sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-top-navigation-by-user-activation"
          src={toAppleMusicEmbed(url)}
        />
      );
    }

    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.split('v=')[1]?.split('&')[0] || url.split('/').pop();
      return (
        <div className='post-media'>
          <iframe
            width="100%"
            height="220"
            src={`https://www.youtube.com/embed/${videoId}`}
            title={`youtube-video-${videoId}`}
            allowFullScreen
          />
        </div>
      );
    }

    return (
      <a href={url} target="_blank" rel="noreferrer">
        {url}
      </a>
    );
  };

  const urls = (content.match(/(https?:\/\/[^\s]+)/g) || []);
  const cleanContent = content.replace(/(https?:\/\/[^\s]+)/g, '');

  const visibleComments = comments;

  const CATEGORY_META: Record<string, { label: string; icon: string }> = {
    thoughts: { label: 'Мысли', icon: '✍️' },
    music: { label: 'Музыка', icon: '🎵' },
    video: { label: 'Видео', icon: '🎥' },
    movie: { label: 'Фильмы', icon: '🎬' },
    books: { label: 'Книги', icon: '📚' },
    photos: { label: 'Фото', icon: '📸' },
  };

  const categoryPicker = (type: string) =>
    CATEGORY_META[type]?.icon + ' ' + CATEGORY_META[type]?.label || '✍️ Мысли';

  return (
    <div className={`post-card ${type}`} ref={ref} data-post-id={id} style={{
      ['--glow-y' as any]: glowY
    }}>
      <div className='header flex'>
        <h3>{title}</h3>
        <p className="date">
          {formatDate(created_at)} • #{id}
          <span className={`badge ${type}`}>
            {categoryPicker(type)}
          </span>
        </p>
      </div>

      <p style={{ paddingLeft: 5 }}>{cleanContent}</p>

      {image && (
        <div className="image-wrapper">
          <div
            className="image-bg"
            style={{ backgroundImage: `url(${image})` }}
          />
          <img
            src={image}
            alt="post"
            className="image-main"
            loading="lazy"
          />
        </div>
      )}

      {urls.map((url, i) => (
        <div key={i} style={{ marginTop: 10 }}>
          {renderMedia(url)}
        </div>
      ))}

      <div className="comments-section" style={{ marginTop: 20 }}>
        {/* <h4>Комментарии</h4> */}
        <div className="add-comment" style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
          <input
            type="text"
            placeholder="Добавить комментарий..."
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            style={{ flex: 1, padding: 8, border: '1px solid #ccc' }}
          />
          <button className="send-btn" onClick={handleAddComment}>
            <span className="text">Отправить</span>
            <span className="icon">➤</span>
          </button>
        </div>

        <div className="comments-list flex">
          {/* {comments.length === 0 && <p style={{ color: '#aaa' }}>Комментариев пока нет</p>} */}
          {visibleComments.map(c => (
            <div key={c.id} className="comment flex" style={{ padding: 8, justifyContent: 'space-between' }}>
              <div>
                <p>{c.text}</p>
                <div className='flex'>
                  <span style={{ color: '#aaa', fontSize: 10 }}>
                    {formatDate(c.created_at)}&nbsp;  •
                  </span>
                  <button className='deleteComment'
                    onClick={() => handleDeleteComment(c.id)}>
                    <span style={{ color: '#ab2929ac', fontSize: 10 }}>
                      &nbsp;Удалить
                    </span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* <div className='deletePost flex'>
        <button className='deleteComment'
          onClick={handleDeletePost}>
          <span style={{ color: '#ab2929', fontSize: 12, fontWeight: 'bold', alignSelf: 'center' }}>
            Удалить пост
          </span>
        </button>
      </div> */}
    </div>
  );
});

export default PostCard;